import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Building2Icon,
  CheckCircle2Icon,
  KeyRoundIcon,
  LockIcon,
  MailIcon,
  ShieldCheckIcon,
  UserIcon,
  UsersIcon,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../hooks/useAuth";

const LOGO_URL =
  "https://yswafknmqpwjrsxjslbu.supabase.co/storage/v1/object/public/logos//logosmall.png";

const getFunctionErrorMessage = async (error, fallback) => {
  if (!error) return fallback;

  try {
    if (error.context?.json) {
      const payload = await error.context.json();
      return payload?.error || payload?.message || fallback;
    }
  } catch (parseError) {
    console.warn("Kunde inte läsa Edge Function-felet:", parseError);
  }

  return error.message || fallback;
};

const normalizeCodeForDisplay = (value) =>
  value.toUpperCase().replace(/\s+/g, "").slice(0, 24);

const normalizeGolfIdForDisplay = (value) =>
  value.toUpperCase().replace(/\s+/g, "").slice(0, 20);

export default function Register() {
  const [searchParams] = useSearchParams();
  const accountType =
    searchParams.get("typ") === "club" ? "club" : "private";
  const prefilledName = (searchParams.get("namn") || "").trim();
  const prefilledEmail = (searchParams.get("email") || "")
    .trim()
    .toLowerCase();

  const [fullName, setFullName] = useState(prefilledName);
  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [golfId, setGolfId] = useState("");
  const [acceptedBetaTerms, setAcceptedBetaTerms] = useState(false);
  const [website, setWebsite] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && user) {
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleRegister = async (event) => {
    event.preventDefault();

    if (
      !fullName.trim() ||
      !email.trim() ||
      !password ||
      !confirmPassword ||
      !accessCode.trim()
    ) {
      setErrorMessage("Fyll i samtliga obligatoriska fält.");
      return;
    }

    if (fullName.trim().length < 2) {
      setErrorMessage("Namnet måste innehålla minst två tecken.");
      return;
    }

    if (password.length < 8) {
      setErrorMessage("Lösenordet måste innehålla minst åtta tecken.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Lösenorden matchar inte.");
      return;
    }

    if (!acceptedBetaTerms) {
      setErrorMessage(
        "Du behöver godkänna villkoren för betaversionen innan kontot skapas."
      );
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const normalizedEmail = email.trim().toLowerCase();

      const { data, error } = await supabase.functions.invoke(
        "register-beta-user",
        {
          body: {
            fullName: fullName.trim(),
            email: normalizedEmail,
            password,
            accessCode: accessCode.trim(),
            accountType,
            golfId: golfId.trim(),
            website,
          },
        }
      );

      if (error) {
        const message = await getFunctionErrorMessage(
          error,
          "Kontot kunde inte skapas. Kontrollera åtkomstkoden och försök igen."
        );
        setErrorMessage(message);
        return;
      }

      if (!data?.success) {
        setErrorMessage(
          data?.error ||
            "Kontot kunde inte skapas. Kontrollera åtkomstkoden och försök igen."
        );
        return;
      }

      const { error: loginError } =
        await supabase.auth.signInWithPassword({
          email: normalizedEmail,
          password,
        });

      if (loginError) {
        console.error(
          "Kontot skapades men automatisk inloggning misslyckades:",
          loginError
        );
        setIsComplete(true);
        return;
      }

      navigate("/", { replace: true });
    } catch (error) {
      console.error("Registreringen misslyckades:", error);
      setErrorMessage(
        "Kontot kunde inte skapas. Kontrollera anslutningen och försök igen."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f0e7] px-4">
        <div className="flex items-center gap-3 rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] px-5 py-4 text-[#1f2d26] shadow-sm">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#e5ede7] text-[#183d2d]">
            ⛳
          </span>
          <div>
            <p className="text-sm font-semibold">Laddar HoleInOne</p>
            <p className="text-xs text-[#68716c]">
              Kontrollerar din session…
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f0e7] text-[#1f2d26]">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-32 -top-28 h-96 w-96 rounded-full bg-[#dfe9e1] blur-3xl" />
        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-[#eee2c8] blur-3xl" />
      </div>

      <main className="relative mx-auto grid min-h-screen w-full max-w-6xl gap-6 px-4 py-6 sm:px-6 sm:py-10 lg:grid-cols-[0.86fr_1.14fr] lg:items-stretch">
        <section className="relative hidden overflow-hidden rounded-[28px] bg-[#183d2d] p-8 text-white shadow-[0_22px_65px_rgba(31,45,38,0.16)] lg:flex lg:flex-col lg:p-10">
          <div className="pointer-events-none absolute -right-28 -top-24 h-80 w-80 rounded-full bg-[#b08b45]/22 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-48 -left-24 h-96 w-96 rounded-full bg-white/[0.08] blur-3xl" />

          <div className="relative">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs font-semibold text-white/55 transition hover:text-white"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Till startsidan
            </Link>

            <div className="mt-10 flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/10">
                <img
                  src={LOGO_URL}
                  alt="HoleInOne"
                  className="h-8 w-8 object-contain brightness-125"
                />
              </span>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/50">
                  HoleInOne.se
                </p>
                <p className="mt-0.5 text-sm font-medium text-white/80">
                  Privat beta
                </p>
              </div>
            </div>

            <p className="mt-10 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#dfcc97]">
              Konto med åtkomstkod
            </p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight">
              Din kod låser upp betaversionen.
            </h1>
            <p className="mt-4 max-w-md text-sm leading-7 text-white/58">
              Åtkomstkoden är personlig, används en gång och kopplas till den
              e-postadress som godkänts i betaansökan.
            </p>
          </div>

          <div className="relative mt-10 space-y-3">
            {[
              "Skapa och administrera golftävlingar",
              "Dela live scorekort till spelarna",
              "Följ individuella resultat, lag och sidotävlingar",
            ].map((item, index) => (
              <div
                key={item}
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.055] p-4"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#d8c38d]/15 text-xs font-semibold text-[#e1cf9a]">
                  {index + 1}
                </span>
                <p className="text-sm font-medium text-white/75">{item}</p>
              </div>
            ))}
          </div>

          <div className="relative mt-auto pt-10">
            <div className="rounded-2xl border border-white/10 bg-white/[0.055] p-4">
              <div className="flex items-start gap-3">
                <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#dcc78f]" />
                <p className="text-xs leading-5 text-white/48">
                  Saknar du kod behöver du först skicka en betaansökan. Godkända
                  användare får en personlig kod via e-post.
                </p>
              </div>
              <Link
                to="/ansok-beta"
                className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#dfcc97] transition hover:text-white"
              >
                Ansök om åtkomst
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[28px] border border-[#d8d2c4] bg-[#fffdf8] shadow-[0_22px_65px_rgba(31,45,38,0.1)]">
          <div className="border-b border-[#e4ded1] bg-[#f7f4ec] px-5 py-4 sm:px-8">
            <div className="flex items-center justify-between gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-xs font-semibold text-[#68716c] transition hover:text-[#183d2d] lg:hidden"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Startsidan
              </Link>

              <div className="ml-auto inline-flex rounded-xl border border-[#d8d2c4] bg-white p-1">
                <span
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[10px] font-semibold ${
                    accountType === "private"
                      ? "bg-[#183d2d] text-white"
                      : "text-[#7b817d]"
                  }`}
                >
                  <UsersIcon className="h-3.5 w-3.5" />
                  Privatperson
                </span>
                <span
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[10px] font-semibold ${
                    accountType === "club"
                      ? "bg-[#183d2d] text-white"
                      : "text-[#7b817d]"
                  }`}
                >
                  <Building2Icon className="h-3.5 w-3.5" />
                  Golfklubb
                </span>
              </div>
            </div>
          </div>

          {isComplete ? (
            <div className="flex min-h-[640px] flex-col items-center justify-center px-6 py-14 text-center sm:px-10">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#e6f0e9] text-[#287148]">
                <CheckCircle2Icon className="h-8 w-8" />
              </span>
              <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9a7735]">
                Kontot är skapat
              </p>
              <h2 className="mt-3 text-2xl font-semibold text-[#183d2d] sm:text-3xl">
                Välkommen till HoleInOne.
              </h2>
              <p className="mt-4 max-w-md text-sm leading-7 text-[#68716c]">
                Kontot skapades, men den automatiska inloggningen kunde inte
                slutföras. Logga in med e-postadressen och lösenordet du nyss
                valde.
              </p>
              <Link
                to="/login"
                className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#214d39]"
              >
                Gå till inloggningen
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <form onSubmit={handleRegister} className="px-5 py-7 sm:px-8 sm:py-9">
              <div className="flex items-center gap-3 lg:hidden">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#183d2d]">
                  <img
                    src={LOGO_URL}
                    alt="HoleInOne"
                    className="h-7 w-7 object-contain brightness-125"
                  />
                </span>
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.25em] text-[#9a7735]">
                    HoleInOne.se
                  </p>
                  <p className="text-sm font-semibold text-[#183d2d]">
                    Privat beta
                  </p>
                </div>
              </div>

              <div className="mt-7 border-b border-[#e4ded1] pb-6 lg:mt-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9a7735]">
                  Personlig åtkomstkod
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-[#183d2d] sm:text-3xl">
                  Skapa ditt konto
                </h2>
                <p className="mt-2 text-sm leading-6 text-[#68716c]">
                  Koden kontrolleras säkert när kontot skapas och kan därefter
                  inte användas igen.
                </p>
              </div>

              <div className="mt-6 space-y-4">
                <div>
                  <label
                    htmlFor="register-code"
                    className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#68716c]"
                  >
                    Åtkomstkod
                  </label>
                  <div className="relative">
                    <KeyRoundIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9a7735]" />
                    <input
                      id="register-code"
                      type="text"
                      autoComplete="one-time-code"
                      spellCheck="false"
                      placeholder="HIO-XXXXXXXX-XXXXXXXX"
                      value={accessCode}
                      onChange={(event) =>
                        setAccessCode(
                          normalizeCodeForDisplay(event.target.value)
                        )
                      }
                      className="w-full rounded-xl border border-[#b9aa8d] bg-[#fffaf0] py-3 pl-10 pr-3 font-mono text-sm font-semibold uppercase tracking-[0.08em] text-[#183d2d] outline-none placeholder:font-sans placeholder:font-normal placeholder:tracking-normal placeholder:text-[#b2aa9d] focus:border-[#9a7735] focus:ring-2 focus:ring-[#b08b45]/12"
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="register-name"
                      className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#68716c]"
                    >
                      Namn
                    </label>
                    <div className="relative">
                      <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d948f]" />
                      <input
                        id="register-name"
                        type="text"
                        autoComplete="name"
                        placeholder="För- och efternamn"
                        value={fullName}
                        readOnly={Boolean(prefilledName)}
                        onChange={(event) => setFullName(event.target.value)}
                        className={`w-full rounded-xl border border-[#c9c1b2] py-3 pl-10 pr-3 text-sm outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10 ${
                          prefilledName
                            ? "cursor-not-allowed bg-[#f3f0e7] text-[#5f6863]"
                            : "bg-white"
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="register-email"
                      className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#68716c]"
                    >
                      E-postadress
                    </label>
                    <div className="relative">
                      <MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d948f]" />
                      <input
                        id="register-email"
                        type="email"
                        autoComplete="email"
                        placeholder="namn@exempel.se"
                        value={email}
                        readOnly={Boolean(prefilledEmail)}
                        onChange={(event) => setEmail(event.target.value)}
                        className={`w-full rounded-xl border border-[#c9c1b2] py-3 pl-10 pr-3 text-sm outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10 ${
                          prefilledEmail
                            ? "cursor-not-allowed bg-[#f3f0e7] text-[#5f6863]"
                            : "bg-white"
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {(prefilledName || prefilledEmail) && (
                  <div className="flex items-start gap-3 rounded-xl border border-[#d9d2c5] bg-[#f7f4ec] px-3.5 py-3 text-xs leading-5 text-[#68716c]">
                    <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#9a7735]" />
                    <p>
                      Namn och e-post är hämtade från den godkända ansökan och
                      kopplade till din personliga åtkomstkod.
                    </p>
                  </div>
                )}

                <div>
                  <label
                    htmlFor="register-golf-id"
                    className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#68716c]"
                  >
                    Golf-ID <span className="normal-case tracking-normal text-[#9aa09c]">(valfritt)</span>
                  </label>
                  <div className="relative">
                    <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d948f]" />
                    <input
                      id="register-golf-id"
                      type="text"
                      autoComplete="off"
                      spellCheck="false"
                      placeholder="Exempel: ÅÅMMDD-XXX"
                      value={golfId}
                      onChange={(event) =>
                        setGolfId(normalizeGolfIdForDisplay(event.target.value))
                      }
                      className="w-full rounded-xl border border-[#c9c1b2] bg-white py-3 pl-10 pr-3 text-sm uppercase outline-none placeholder:normal-case placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                    />
                  </div>
                  <p className="mt-1.5 text-[11px] leading-5 text-[#8a8f8b]">
                    Sparas privat för en eventuell framtida koppling till golfens IT-system.
                  </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="register-password"
                      className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#68716c]"
                    >
                      Lösenord
                    </label>
                    <div className="relative">
                      <LockIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d948f]" />
                      <input
                        id="register-password"
                        type="password"
                        autoComplete="new-password"
                        placeholder="Minst 8 tecken"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        className="w-full rounded-xl border border-[#c9c1b2] bg-white py-3 pl-10 pr-3 text-sm outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="register-confirm-password"
                      className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#68716c]"
                    >
                      Upprepa lösenord
                    </label>
                    <div className="relative">
                      <LockIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d948f]" />
                      <input
                        id="register-confirm-password"
                        type="password"
                        autoComplete="new-password"
                        placeholder="Samma lösenord igen"
                        value={confirmPassword}
                        onChange={(event) =>
                          setConfirmPassword(event.target.value)
                        }
                        className="w-full rounded-xl border border-[#c9c1b2] bg-white py-3 pl-10 pr-3 text-sm outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                      />
                    </div>
                  </div>
                </div>

                <div className="hidden" aria-hidden="true">
                  <label htmlFor="register-website">Webbplats</label>
                  <input
                    id="register-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(event) => setWebsite(event.target.value)}
                  />
                </div>

                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#e0d9cc] bg-[#f8f5ed] p-3.5">
                  <input
                    type="checkbox"
                    checked={acceptedBetaTerms}
                    onChange={(event) =>
                      setAcceptedBetaTerms(event.target.checked)
                    }
                    className="mt-0.5 h-4 w-4 accent-[#183d2d]"
                  />
                  <span className="text-xs leading-5 text-[#68716c]">
                    Jag förstår att HoleInOne är en betaversion där funktioner
                    kan förändras och där fel fortfarande kan förekomma.
                  </span>
                </label>

                {errorMessage && (
                  <div
                    role="alert"
                    className="flex items-start gap-3 rounded-xl border border-[#dfc5c0] bg-[#fff8f6] px-3.5 py-3 text-sm text-[#a0443e]"
                  >
                    <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    <p className="leading-5">{errorMessage}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(24,61,45,0.17)] transition hover:bg-[#214d39] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? "Kontrollerar kod och skapar konto…" : "Skapa konto"}
                  {!isSubmitting && (
                    <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  )}
                </button>

                <div className="flex flex-col items-center justify-between gap-2 border-t border-[#e4ded1] pt-4 text-xs text-[#68716c] sm:flex-row">
                  <p>
                    Har du redan ett konto?{" "}
                    <Link
                      to="/login"
                      className="font-semibold text-[#183d2d] hover:text-[#9a7735] hover:underline"
                    >
                      Logga in
                    </Link>
                  </p>
                  <Link
                    to="/ansok-beta"
                    className="font-semibold text-[#183d2d] hover:text-[#9a7735] hover:underline"
                  >
                    Saknar du kod?
                  </Link>
                </div>
              </div>
            </form>
          )}

          <footer className="border-t border-[#e4ded1] bg-[#f7f4ec] px-5 py-4 text-center text-[10px] text-[#7b817d]">
            © {new Date().getFullYear()} HoleInOne.se · En tjänst från Hollander Web
          </footer>
        </section>
      </main>
    </div>
  );
}